import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities, CmsModel } from 'client/data/models/cms';
import { DEFAULT_CONTENT, parseContent } from 'client/data/cms/content';
import { TrackingHandler } from 'client/tracking/handler';
import { CommonMap } from 'client/tracking/maps/common';
import { getTypeBeforeBuyContentPath } from 'site-modules/editorial/utils/content-path';
import { getTypeBeforeBuyBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import {
  MOST_RESEARCHED_VEHICLES_CONTENT_PATH,
  NEW_CAR_RESOURCES_CONTENT_PATH,
} from 'client/data/constants/contents-paths';
import { parseContentListLinks } from 'site-modules/shared/utils/parse-content-metadata';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { TypeJellybeanNavV2 } from 'site-modules/shared/components/type-jellybean-nav/type-jellybean-nav-v2';
import { ArticleMenu } from 'site-modules/shared/components/editorial/article-menu/article-menu';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { NativeVideoAd } from 'site-modules/shared/components/native-ad/video/video-native-ad';

import './type-before-buy.scss';

const ARTICLE_MENU_STYLE_PROPS = {
  dividerClassName: 'd-none',
  titleContainerClassName: 'p-1 bg-gray-lighter',
  titleClassName: 'heading-5',
  listClassName: 'bg-gray-extra-light px-1 mb-1_5',
  itemsClassName: 'article-menu-item py-1',
  linksClassName: 'heading-5 fw-normal',
  moreLinkClassName: 'heading-5 fw-normal text-primary-darker',
};

function TypeBeforeBuyUI({
  type,
  content = null,
  newCarResourcesContent = DEFAULT_CONTENT,
  mostResearchedVehiclesContent = DEFAULT_CONTENT,
}) {
  useEffect(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    TrackingHandler.useMap(CommonMap);
  }, []);

  if (!content) {
    return null;
  }

  const { title } = content;
  const htmlContent = content
    .child('sub-page-1')
    .child('sub-page-1-main-container')
    .child('what-you-should-know-pod')
    .child('what-you-should-know-container').content;

  const newCarResourcesLinks = parseContentListLinks(newCarResourcesContent);
  const mostResearchedVehiclesLinks = parseContentListLinks(mostResearchedVehiclesContent);

  return (
    <Container tag="main" id="main-content" tabIndex={-1} className="type-before-buy">
      <Breadcrumbs data={getTypeBeforeBuyBreadcrumbs({ type, title })} className="ps-0" />
      <h1 className="heading-2 mb-1_5">{title}</h1>
      <Row>
        <Col xs={12} lg={8} className="mb-1_5">
          <ContentFragment classes="before-buy-content text-gray-darker">{htmlContent}</ContentFragment>
        </Col>
        <Col xs={12} lg={4} className="mb-1_5 mb-lg-0">
          <ArticleMenu {...mostResearchedVehiclesLinks} {...ARTICLE_MENU_STYLE_PROPS} />
          <MrectNative position={'1'} refreshable={false} wrapperClass="mb-2" />
          <ArticleMenu {...newCarResourcesLinks} {...ARTICLE_MENU_STYLE_PROPS} />
          <MrectNative position={'2'} refreshable={false} wrapperClass="mb-2" />
          <TypeJellybeanNavV2 headingClassName="heading-5" includeSymbolSheet />
          <NativeVideoAd wrapperClass="my-2" />
        </Col>
      </Row>
    </Container>
  );
}

TypeBeforeBuyUI.propTypes = {
  type: PropTypes.string.isRequired,
  content: CmsEntities.Content,
  newCarResourcesContent: CmsEntities.Content,
  mostResearchedVehiclesContent: CmsEntities.Content,
};

export const TypeBeforeBuy = connectToModel(TypeBeforeBuyUI, {
  content: bindToPath(
    ({ type }) => `content["${getTypeBeforeBuyContentPath(type)}.withContent"]`,
    CmsModel,
    parseContent
  ),
  newCarResourcesContent: bindToContent(NEW_CAR_RESOURCES_CONTENT_PATH, 'content-list'),
  mostResearchedVehiclesContent: bindToContent(MOST_RESEARCHED_VEHICLES_CONTENT_PATH, 'content-list'),
});
